import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Star, CheckCircle2, MessageSquare, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function ConfirmRatingPage() {
    const { ratingId } = useParams();
    const navigate = useNavigate();
    const [rating, setRating] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadRating();
    }, [ratingId]);

    const loadRating = async () => {
        try {
            const pendingRes = await api.get('/ratings/my-pending');
            const foundRating = pendingRes.data.ratings?.find(r => r.id === ratingId);
            
            if (!foundRating || foundRating.status !== 'DISPUTED') {
                toast.error('Avis non trouvé ou pas en attente de confirmation');
                navigate('/seller');
                return;
            }

            setRating(foundRating);
        } catch (error) {
            console.error('Error loading rating:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (maintain) => {
        setSubmitting(true);
        try {
            await api.post(`/ratings/${ratingId}/confirm`, { maintain });
            
            toast.success(maintain 
                ? 'Votre avis a été maintenu et publié.' 
                : 'Merci d\'avoir pris en compte la réponse de l\'agent.'
            );
            navigate('/seller');
        } catch (error) {
            console.error('Error confirming rating:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner w-8 h-8"></div>
            </div>
        );
    }

    if (!rating) return null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#681793]/5 to-[#FF914D]/5 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2">L'agent a répondu</h1>
                    <p className="text-muted-foreground">
                        Prenez connaissance de sa réponse et confirmez votre avis
                    </p>
                </div>

                {/* Your Rating */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Votre avis</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-2 mb-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                    key={star}
                                    className={cn(
                                        'w-5 h-5',
                                        star <= Math.round(rating.overall)
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'text-gray-300'
                                    )}
                                />
                            ))}
                            <span className="font-bold ml-2">{rating.overall.toFixed(1)}/5</span>
                        </div>
                        {rating.comment && (
                            <p className="text-muted-foreground italic">"{rating.comment}"</p>
                        )}
                    </CardContent>
                </Card>

                {/* Agent Response */}
                {rating.agent_response && (
                    <Card className="mb-6 border-[#681793]/30 bg-[#681793]/5">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-[#681793]" />
                                Réponse de l'agent
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-foreground">
                                {rating.agent_response.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                Répondu le {new Date(rating.agent_response.created_at).toLocaleDateString('fr-FR')}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Actions */}
                <Card>
                    <CardHeader>
                        <CardTitle>Que souhaitez-vous faire ?</CardTitle>
                        <CardDescription>
                            Votre décision est définitive et votre avis sera publié
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Button
                            onClick={() => handleConfirm(true)}
                            disabled={submitting}
                            variant="outline"
                            className="w-full h-auto py-4 flex flex-col items-start text-left border-orange-200 hover:bg-orange-50"
                        >
                            <div className="flex items-center gap-2 font-semibold">
                                <AlertTriangle className="w-5 h-5 text-orange-500" />
                                Maintenir mon avis
                            </div>
                            <p className="text-sm text-muted-foreground font-normal mt-1">
                                Je maintiens ma notation malgré la réponse de l'agent
                            </p>
                        </Button>

                        <Button
                            onClick={() => handleConfirm(false)}
                            disabled={submitting}
                            className="w-full h-auto py-4 flex flex-col items-start text-left bg-green-600 hover:bg-green-700"
                        >
                            <div className="flex items-center gap-2 font-semibold">
                                <CheckCircle2 className="w-5 h-5" />
                                Accepter la réponse
                            </div>
                            <p className="text-sm text-green-100 font-normal mt-1">
                                Je prends en compte la réponse de l'agent, mon avis est publié tel quel
                            </p>
                        </Button>

                        {submitting && (
                            <div className="flex items-center justify-center py-2">
                                <div className="spinner w-5 h-5 mr-2"></div>
                                <span className="text-sm text-muted-foreground">Traitement en cours...</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
